import { useCallback, useMemo, useRef, useState } from 'react';
import { DonorGiving } from '@signalvote/shared';
import type { FinanceContactDto } from '@signalvote/shared';
import { useFinanceContact, useFinanceContacts } from '@/hooks/queries/useFinanceContacts';

const EMPTY: FinanceContactDto[] = [];

/**
 * Contacts for a picker that records money coming in (a contribution, its reattribution, a loan).
 *
 * Defaults to people who have given, so an uploaded prospect list can't push real donors off the
 * end of the loaded page. When a search matches none of them it falls through to donors who have
 * not given, returned as `prospects` for the combobox to show in their own group — so a prospect
 * is findable the moment their first cheque arrives, instead of being re-created as a duplicate.
 *
 * `contacts` also carries the selected contact when it isn't a giver — picked from a search,
 * just quick-created, or pre-selected as a suggestion — so the trigger and callers' own lookups
 * by id keep working.
 */
export function useDonorPickerContacts(selectedId: string) {
  const [search, setSearch] = useState('');
  const { data } = useFinanceContacts({ pageSize: 100, donorGiving: DonorGiving.HasGiven });
  const givers = data?.items ?? EMPTY;

  const term = search.trim().toLowerCase();
  const noGiverMatches = term !== '' && !givers.some((c) => c.name.toLowerCase().includes(term));
  const { data: prospectData } = useFinanceContacts(
    { pageSize: 20, donorGiving: DonorGiving.NotGiven, search: search.trim() },
    { enabled: noGiverMatches },
  );
  const prospects = noGiverMatches ? (prospectData?.items ?? EMPTY) : EMPTY;

  // Remember every prospect we've shown, so a picked one outlives the search that found them.
  const seenProspects = useRef(new Map<string, FinanceContactDto>());
  for (const p of prospects) seenProspects.current.set(p.id, p);
  const selectedIsGiver = givers.some((c) => c.id === selectedId);
  const seenSelected = selectedIsGiver ? undefined : seenProspects.current.get(selectedId);
  // Selected but never listed here: fetch it by id (an empty id leaves the query disabled).
  const { data: fetchedSelected } = useFinanceContact(
    selectedId && !selectedIsGiver && !seenSelected ? selectedId : '',
  );
  const pickedProspect = selectedIsGiver ? undefined : (seenSelected ?? fetchedSelected ?? undefined);

  const contacts = useMemo(
    () => (pickedProspect ? [...givers, pickedProspect] : givers),
    [givers, pickedProspect],
  );

  const onSearchChange = useCallback((value: string) => setSearch(value), []);

  return { contacts, prospects, onSearchChange };
}
